/****************************************************
 * Module Name:  createApproveRejectExcelFile.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import * as XLSX from "sheetjs-style";

export const createApproveRejectExcelFile = async (
    excelData,
    columns,
    filename,
    symMasterMap,
    polMasterMap,
    floMasterMap,
    cutMasterMap,
    clarityMasterMap,
    colorMasterMap
) => {

    const COLORS = {
        headerBg: "304674",
        headerText: "FFFFFFFF",
        totalBg: "FFEFEFEF",
        default: "00000000",
        whiteCell: "FFFFFFFF",
        redCell: "FFF5D7D7",
        darkRedCell: "FFF98D85",
        greenCell: "FFB6D7A8",
        yellowCell: "FFF1E68C",
    };

    // Prepare data for export based on provided columns
    const columnKeys = columns.map(col => col.key); // will display just the columns with keys
    const filteredData = excelData.map(row => Object.fromEntries(columnKeys.map(key => [key, row[key]])));

    // Convert filtered JSON data to sheet
    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    worksheet["!cols"] = columns.map(col => ({ wch: col.width || 10 }));

    // Add header row manually
    columns.forEach((col, colIndex) => {
        const cellRef = XLSX.utils.encode_cell({ r: 0, c: colIndex });

        // Default header style
        let cellStyle = {
            fill: { patternType: "solid", fgColor: { rgb: COLORS.headerBg } },
            font: { bold: true, color: { rgb: COLORS.headerText }, name: "Cambria", sz: 11, },
            alignment: { horizontal: "center" },
            border: {
                top: { style: "thin", color: { rgb: COLORS.default } },
                bottom: { style: "thin", color: { rgb: COLORS.default } },
                left: { style: "thin", color: { rgb: COLORS.default } },
                right: { style: "thin", color: { rgb: COLORS.default } },
            },
        };

        worksheet[cellRef] = {
            t: "s",
            v: col.label,
            s: cellStyle,
        };
    });

    // Process each data row for formatting and styling
    excelData.forEach((item, rowIndex) => {
        const actualRow = rowIndex + 1;
        const isTotalRow = rowIndex === excelData.length - 1;
        let isBold = false;

        const TWO_DECIMAL_COLUMNS = new Set(["MFCTS", "CTS", "DIS", "PPC", "MFPPC", "TOTAL", "MFTOTAL"]);
        const MF_KEY_NAMES = new Set(["MFCTS", "MFCOL", "MFCOLDESC", "MFCLR", "MFCUT", "MFPOL", "MFSYM", "MFFLR", "MFRAP", "MFPPC", "MFTOTAL"]);
        const COMPARISON_LAB_COLUMNS = new Set(["CTS", "COL", "COLDESC", "CLR", "CUT", "POL", "SYM", "FLR", "RAP", "PPC", "TOTAL"]);

        Object.keys(item).forEach((key, colIndex) => {

            const value = item[key];
            const isNumeric = !isNaN(value) && value !== "" && value !== null;

            const cellRef = XLSX.utils.encode_cell({ r: actualRow, c: colIndex });
            worksheet[cellRef] = worksheet[cellRef] || {};

            let fillStyle = {};

            // logic for making cutpolsym bold
            if (key === "CUT" && item && !isTotalRow) {
                isBold = ["ID", "EX"].includes(item["CUT"]) &&
                    ["ID", "EX"].includes(item["POL"]) &&
                    ["ID", "EX"].includes(item["SYM"]);
            }
            // logic for last row's value and style and formula fromatting
            else if (isTotalRow) {
                fillStyle = {
                    fill: {
                        patternType: "solid",
                        fgColor: { rgb: COLORS.totalBg },
                    },
                };

                const totalCol = columns.findIndex(col => col.key === "TOTAL");
                const ctsCol = columns.findIndex(col => col.key === "CTS");
                const priceCol = columns.findIndex(col => col.key === "PPC");
                const disCol = columns.findIndex(col => col.key === "DIS");

                //  excel formulas for last record
                const columnLetter = XLSX.utils.encode_col(colIndex);
                if (key === "ID") {
                    worksheet[cellRef].f = `COUNTA(${columnLetter}${2}:${columnLetter}${excelData.length})`;
                    worksheet[cellRef].v = Number(item["ID"]);
                } else if (key === "CTS" || key === "TOTAL") {
                    worksheet[cellRef].f = `ROUND(SUM(${columnLetter}2:${columnLetter}${excelData.length}), 2)`;
                    worksheet[cellRef].v = key === "CTS" ? Number(item["CTS"]) : Number(item["TOTAL"]);
                } else if (key === "PPC") {
                    const totalLetter = XLSX.utils.encode_col(totalCol);
                    const ctsLetter = XLSX.utils.encode_col(ctsCol);

                    worksheet[cellRef].f = `IF(${ctsLetter}${excelData.length + 1}=0, 0, ROUND(${totalLetter}${excelData.length + 1}/${ctsLetter}${excelData.length + 1}, 2))`;
                    worksheet[cellRef].v = Number(item["PPC"]);
                } else if (key === "DIS") {

                    const discountLetter = XLSX.utils.encode_col(disCol);
                    const priceLetter = XLSX.utils.encode_col(priceCol);
                    const totalLetter = XLSX.utils.encode_col(totalCol);
                    const ctsLetter = XLSX.utils.encode_col(ctsCol);

                    worksheet[cellRef].f = `ROUND((100 - (SUM(${totalLetter}${2}:${totalLetter}${excelData.length}) / 
                                    SUMPRODUCT(${ctsLetter}${2}:${ctsLetter}${excelData.length}, (100 * ${priceLetter}${2}:${priceLetter}${excelData.length}) 
                                    / (100 + ${discountLetter}${2}:${discountLetter}${excelData.length})) )  * 100)  * -1,2)`

                    worksheet[cellRef].v = item["DIS"];
                }
                else if (key === "MFCTS") {
                    worksheet[cellRef].f = `ROUND(SUM(${columnLetter}2:${columnLetter}${excelData.length}), 2)`;
                    worksheet[cellRef].v = Number(item["MFCTS"]);
                } else if (key === "MFTOTAL") {
                    worksheet[cellRef].f = `ROUND(SUM(${columnLetter}2:${columnLetter}${excelData.length}), 2)`;
                    worksheet[cellRef].v = Number(item["MFTOTAL"]);
                } else if (key === "MFPPC") {
                    const mfTotalCol = columns.findIndex(col => col.key === "MFTOTAL");
                    const mfCtsCol = columns.findIndex(col => col.key === "MFCTS");
                    const mfTotalLetter = XLSX.utils.encode_col(mfTotalCol);
                    const mfCtsLetter = XLSX.utils.encode_col(mfCtsCol);

                    worksheet[cellRef].f = `IF(${mfCtsLetter}${excelData.length + 1}=0, 0, ROUND(${mfTotalLetter}${excelData.length + 1}/${mfCtsLetter}${excelData.length + 1}, 2))`;
                    worksheet[cellRef].v = Number(item["MFPPC"]);
                }
            }

            // Format numbers as decimals (two places) for specific columns
            if (TWO_DECIMAL_COLUMNS.has(key)) {
                worksheet[cellRef].t = isNumeric ? "n" : "s";  // n for number, s for string
                if (isNumeric) {
                    worksheet[cellRef].z = "0.00"; // Excel number format with 2 decimal places
                }
            }

            // Apply cell-level styles
            worksheet[cellRef].s = {
                ...fillStyle,
                font: {
                    name: "Cambria",
                    sz: 11,
                    bold: isTotalRow || ((key === "CUT" || key === "POL" || key === "SYM") && isBold) || key === "DIS",
                    color: {
                        rgb: key === "MEDIA" ? "0061FF" : key === "DIS" ? "ED2939" : COLORS.default,
                    },
                    underline: key === "MEDIA" ? true : false,
                },
                alignment: { horizontal: TWO_DECIMAL_COLUMNS.has(key) ? "right" : "center" },
                border: {
                    top: { style: "thin", color: { rgb: COLORS.default } },
                    bottom: { style: "thin", color: { rgb: COLORS.default } },
                    left: { style: "thin", color: { rgb: COLORS.default } },
                    right: { style: "thin", color: { rgb: COLORS.default } },
                },
            };

            // Highlighthing Manufacuring Columns with Light Red Color
            if (MF_KEY_NAMES.has(key)) {
                worksheet[cellRef].s = {
                    ...worksheet[cellRef].s,
                    fill: {
                        patternType: "solid",
                        fgColor: { rgb: COLORS.redCell } // Red background
                    }
                }
            }

            // Highlighting Green Color if lab value is better than mf value
            if (COMPARISON_LAB_COLUMNS.has(key)) {

                let greenCell = (key === "CTS" && item["MFCTS"] < item["CTS"]) ||
                    (key === "COL" && colorMasterMap[item["MFCOL"]] > colorMasterMap[item["COL"]]) ||
                    (key === "CLR" && clarityMasterMap[item["MFCLR"]] > clarityMasterMap[item["CLR"]]) ||
                    (key === "CUT" && cutMasterMap[item["MFCUT"]] < cutMasterMap[item["CUT"]]) ||
                    (key === "POL" && polMasterMap[item["MFPOL"]] < polMasterMap[item["POL"]]) ||
                    (key === "SYM" && symMasterMap[item["MFSYM"]] < symMasterMap[item["SYM"]]) ||
                    (key === "FLR" && floMasterMap[item["MFFLR"]] < floMasterMap[item["FLR"]]) ||
                    (key === "RAP" && item["MFRAP"] < item["RAP"]) ||
                    (key === "PPC" && item["MFPPC"] < item["PPC"]) ||
                    (key === "TOTAL" && item["MFTOTAL"] < item["TOTAL"])

                let redCell = (key === "CTS" && item["MFCTS"] > item["CTS"]) ||
                    (key === "COL" && colorMasterMap[item["COL"]] - colorMasterMap[item["MFCOL"]] > 1)

                let yellowCell = (key === "COL" && colorMasterMap[item["COL"]] - colorMasterMap[item["MFCOL"]] === 1) ||
                    (key === "COLDESC" && !((item["MFCOLDESC"] === "" || item["MFCOLDESC"] === null) && (item["COLDESC"] === "" || item["COLDESC"] === null))) ||
                    (key === "CLR" && clarityMasterMap[item["MFCLR"]] < clarityMasterMap[item["CLR"]]) ||
                    (key === "CUT" && cutMasterMap[item["MFCUT"]] > cutMasterMap[item["CUT"]]) ||
                    (key === "POL" && polMasterMap[item["MFPOL"]] > polMasterMap[item["POL"]]) ||
                    (key === "SYM" && symMasterMap[item["MFSYM"]] > symMasterMap[item["SYM"]]) ||
                    (key === "FLR" && floMasterMap[item["MFFLR"]] > floMasterMap[item["FLR"]]) ||
                    (key === "RAP" && item["MFRAP"] > item["RAP"]) ||
                    (key === "PPC" && item["MFPPC"] > item["PPC"]) ||
                    (key === "TOTAL" && item["MFTOTAL"] > item["TOTAL"])

                worksheet[cellRef].s = {
                    ...worksheet[cellRef].s,
                    fill: {
                        patternType: "solid",
                        fgColor: { rgb: greenCell ? COLORS.greenCell : (yellowCell ? COLORS.yellowCell : (redCell ? COLORS.darkRedCell : COLORS.whiteCell)) } // Red background
                    }
                }
            }

            if (isTotalRow) {
                worksheet[cellRef].s = {
                    ...worksheet[cellRef].s,
                    fill: {
                        patternType: "solid",
                        fgColor: { rgb: COLORS.totalBg } // background
                    }
                }
            }
        });
    });

    //initiate file download
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, worksheet, "Diamond Data");

    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], { type: "application/octet-stream" });

    // browser download
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};
